'use client'
import Link from 'next/link'
import { usePathname } from 'next/navigation'

export interface LinkGroupItem {
  label: string
  href?: string
}

interface InlineLinkListProps {
  id?: string
  heading: string
  items: LinkGroupItem[]
  // These lists run to 85+ links and render in every page's footer; prefetching
  // fires an RSC request per link and trips the per-IP Cloud Armor rate limit.
  // Defaults off; opt in per-list where prefetching is genuinely worth it.
  prefetch?: boolean
}

const normalizePath = (path: string): string => path.replace(/\/+$/, '') || '/'

// Path via usePathname(), not headers(): the footer lives in the root layout, which Next never re-renders on client-side navigation.
const InlineLinkList = ({
  id,
  heading,
  items,
  prefetch = false,
}: InlineLinkListProps) => {
  const currentPath = normalizePath(usePathname() || '')

  const visibleItems = items?.filter(
    (item) => !item.href || normalizePath(item.href) !== currentPath
  )

  if (!visibleItems || visibleItems.length === 0) return null

  return (
    <section id={id} className="py-6 text-center">
      <h3 className="tracking-tight text-heading-sm lg:text-heading-md font-semibold text-gray-900 mb-3 lg:mb-5">
        {heading}
      </h3>
      <div className="flex flex-wrap items-center justify-center divide-x divide-gray-100 gap-y-1">
        {visibleItems.map((item, index) =>
          item.href ? (
            <Link
              key={`${item.label}-${index}`}
              href={item.href}
              prefetch={prefetch}
              className="text-body-xs lg:text-body-sm text-gray-600 font-medium leading-4 lg:leading-6 px-3 my-0.5 hover:text-primary-600"
            >
              {item.label}
            </Link>
          ) : (
            // No href: plain text, so it is not focusable or clickable.
            <span
              key={`${item.label}-${index}`}
              className="text-body-xs lg:text-body-sm text-gray-600 font-medium leading-4 lg:leading-6 px-3 my-0.5"
            >
              {item.label}
            </span>
          )
        )}
      </div>
    </section>
  )
}

export default InlineLinkList
